import { createContext } from 'react';

/**
 * Base interface for all integration providers
 */
export interface BaseIntegration {
  id: string;
  name: string;
  type: string;
  enabled: boolean;
  createdAt: number;
  updatedAt: number;
}

/**
 * Stripe Connect integration using OAuth 2.0
 * Stores access tokens and metadata from Stripe Connect
 */
export interface StripeIntegration extends BaseIntegration {
  type: 'stripe';
  /** Stripe Connect access token (encrypted at rest) */
  accessToken: string;
  /** Stripe account ID (e.g., acct_xxx) */
  stripeAccountId: string;
  /** Stripe publishable key for client-side use */
  publishableKey: string;
  /** Whether this is a test mode account */
  livemode: boolean;
  /** Token scopes granted by the user */
  scopes?: string[];
  /** Refresh token for renewing access (if provided by Stripe) */
  refreshToken?: string;
  /** Additional metadata from Stripe */
  metadata?: {
    stripeUserId?: string;
    businessName?: string;
    email?: string;
    country?: string;
    [key: string]: unknown;
  };
}

/**
 * Supabase integration
 * Stores connection details for Supabase projects
 */
export interface SupabaseIntegration extends BaseIntegration {
  type: 'supabase';
  /** Supabase project URL (e.g., https://xxxxx.supabase.co) */
  projectUrl: string;
  /** Public anon key (encrypted at rest) */
  anonKey: string;
  /** Service role key for admin operations (encrypted at rest, optional) */
  serviceRoleKey?: string;
  /** Project reference ID (e.g., xxxxx from the URL) */
  projectRef: string;
  /** Additional metadata */
  metadata?: {
    projectName?: string;
    region?: string;
    [key: string]: unknown;
  };
}

/**
 * Union type of all supported integrations
 * Add new integration types here as they're implemented
 */
export type Integration = StripeIntegration | SupabaseIntegration;

/**
 * Integration settings stored in VFS
 */
export interface IntegrationsSettings {
  integrations: Integration[];
  /** Per-project integration associations */
  projectIntegrations?: Record<string, string[]>; // projectId -> integrationIds[]
}

/**
 * Context for managing integrations
 */
export interface IntegrationsContextType {
  settings: IntegrationsSettings;
  updateSettings: (settings: Partial<IntegrationsSettings>) => void;
  addIntegration: (integration: Integration) => void;
  removeIntegration: (integrationId: string) => void;
  updateIntegration: (integrationId: string, updates: Partial<Integration>) => void;
  getIntegration: (integrationId: string) => Integration | undefined;
  getIntegrationsByType: <T extends Integration>(type: string) => T[];
  getProjectIntegrations: (projectId: string) => Integration[];
  enableIntegrationForProject: (projectId: string, integrationId: string) => void;
  disableIntegrationForProject: (projectId: string, integrationId: string) => void;
  isConfigured: boolean;
  isInitialized: boolean;
}

export const IntegrationsContext = createContext<IntegrationsContextType | undefined>(undefined);
